import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaLaptopCode, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Modal from '../Modal';
import { usePortfolio } from '../../context/PortfolioContext';

const Projects = () => {
  const { portfolioData, loading } = usePortfolio();
  const projects = portfolioData?.projects || [];
  const [activeFilter, setActiveFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(3);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  // Collect all unique categories from all projects
  const allCategories = Array.from(
    new Set(
      projects
        .flatMap(p => Array.isArray(p.categories) ? p.categories : [])
        .filter(Boolean)
    )
  );

  // Filtering logic
  const filteredProjects =
    activeFilter === 'all'
      ? projects
      : projects.filter(project =>
          Array.isArray(project.categories) && project.categories.includes(activeFilter)
        );

  const visibleProjects = filteredProjects.filter(project => project.visible !== false);

  // Group projects by category
  const projectsByCategory = allCategories.reduce((acc, cat) => {
    acc[cat] = projects.filter(
      project => Array.isArray(project.categories) && project.categories.includes(cat) && project.visible !== false
    );
    return acc;
  }, {});

  useEffect(() => {
    // In a real app, this would fetch from API or JSON file
    // For now, we'll use mock data
    // const mockData = [
    //   {
    //     id: 1,
    //     title: "AI-Powered Image Recognition System",
    //     description: "A computer vision application that identifies objects in real-time with 98% accuracy. Uses TensorFlow and OpenCV for processing and provides API endpoints for integration.",
    //     longDescription: "This project implements state-of-the-art convolutional neural networks to recognize and classify objects in images and video streams in real-time. The system was trained on a custom dataset of over 100,000 images across 150 categories, achieving 98.3% accuracy on validation data. The application provides both a user-friendly web interface and RESTful API endpoints, making it versatile for various integration scenarios. Key features include multi-object detection, bounded box visualization, and confidence scoring for each detected item.",
    //     image: "/projects/ai-image-recognition.jpg",
    //     tags: ["AI", "Computer Vision", "Python"],
    //     links: {
    //       github: "https://github.com/dhiraj-th/ai-image-recognition",
    //       live: "https://ai-vision-demo.dhirajth.com"
    //     },
    //     technologies: ["TensorFlow", "Python", "OpenCV", "Flask", "React"]
    //   },
    //   {
    //     id: 2,
    //     title: "E-Commerce Platform",
    //     description: "A full-stack e-commerce solution with integrated payment processing, inventory management, and analytics dashboard.",
    //     longDescription: "This comprehensive e-commerce platform provides businesses with everything they need to sell products online. Built on the MERN stack, it features a responsive design, secure payment processing through Stripe, real-time inventory management, and an analytics dashboard for business insights. The platform supports multiple user roles (admin, manager, customer) with appropriate permissions, and includes features like wish lists, reviews, and personalized recommendations based on user behavior. The system is containerized with Docker for easy deployment and scalability.",
    //     image: "/projects/ecommerce-platform.jpg",
    //     tags: ["Web", "Full Stack"],
    //     links: {
    //       github: "https://github.com/dhiraj-th/ecommerce-platform",
    //       live: "https://ecommerce-demo.dhirajth.com"
    //     },
    //     technologies: ["MongoDB", "Express", "React", "Node.js", "Redux", "Stripe API"]
    //   },
    //   {
    //     id: 3,
    //     title: "Smart Blind Stick",
    //     description: "An IoT-enabled walking stick that uses ultrasonic sensors and GPS to guide visually impaired individuals through obstacles and navigation.",
    //     longDescription: "The Smart Blind Stick is an innovative assistive technology designed to improve mobility and independence for visually impaired individuals. The device uses ultrasonic sensors to detect obstacles in multiple directions up to 2 meters away and provides haptic feedback through vibration motors with varying intensities based on proximity. It also incorporates GPS for location tracking, paired with a smartphone app that offers turn-by-turn voice navigation. Additional features include emergency SOS buttons, water detection sensors, and a rechargeable battery that lasts up to 10 hours. The entire system is designed to be lightweight, weather-resistant, and intuitive to use.",
    //     image: "/projects/smart-blind-stick.jpg",
    //     tags: ["Mechanical", "IoT"],
    //     links: {
    //       github: "https://github.com/dhiraj-th/smart-blind-stick",
    //       live: null
    //     },
    //     technologies: ["Arduino", "Ultrasonic Sensors", "GPS", "Bluetooth", "3D Printing"]
    //   },
    //   {
    //     id: 4,
    //     title: "Sentiment Analysis API",
    //     description: "A natural language processing API that analyzes text for sentiment, emotion, and key phrases. Used by marketing teams to understand customer feedback.",
    //     longDescription: "This NLP-powered API offers comprehensive text analysis capabilities, enabling businesses to extract meaningful insights from customer feedback, social media comments, and other text data. The system uses a fine-tuned BERT model for contextual understanding and employs various classification algorithms to detect sentiment (positive, negative, neutral), emotions (joy, anger, surprise, etc.), and extract key topics and phrases. The API is deployed as a scalable cloud service with rate limiting, authentication, and comprehensive documentation. It's designed to handle multiple languages and can process both short texts like tweets and longer documents like reviews or articles.",
    //     image: "/projects/sentiment-analysis.jpg",
    //     tags: ["AI", "NLP"],
    //     links: {
    //       github: "https://github.com/dhiraj-th/sentiment-analysis-api",
    //       live: "https://api-docs.sentiment.dhirajth.com"
    //     },
    //     technologies: ["Python", "NLTK", "SpaCy", "FastAPI", "Docker"]
    //   },
    //   {
    //     id: 5,
    //     title: "JARVIS Editor",
    //     description: "A voice-controlled code editor that allows developers to write, edit, and debug code using natural language commands.",
    //     longDescription: "JARVIS Editor is a revolutionary coding environment that combines the power of AI with voice recognition to create a hands-free programming experience. The system can interpret natural language commands to perform code operations like creating functions, fixing bugs, refactoring code, and even searching documentation. It supports multiple programming languages with syntax-aware editing and provides intelligent autocompletion based on context. The editor includes a visualization mode for debugging and can explain code sections through voice feedback. This project represents a significant step toward making programming more accessible for people with mobility impairments and offers efficiency gains for all developers by reducing reliance on keyboard input.",
    //     image: "/projects/jarvis-editor.jpg",
    //     tags: ["AI", "Web"],
    //     links: {
    //       github: "https://github.com/dhiraj-th/jarvis-editor",
    //       live: "https://jarvis-editor.dhirajth.com"
    //     },
    //     technologies: ["JavaScript", "Speech Recognition API", "OpenAI API", "Electron", "CodeMirror"]
    //   },
    //   {
    //     id: 6,
    //     title: "Automated Hydroponics System",
    //     description: "An IoT-based system that monitors and controls environmental conditions for optimal plant growth in hydroponic farms.",
    //     longDescription: "This automated hydroponics management system combines hardware and software to create an optimal growing environment for plants without soil. The hardware includes sensors for monitoring pH, EC (nutrient concentration), temperature, humidity, water level, and light intensity. Microcontrollers process this data and control pumps, nutrient dispensers, LED grow lights, and climate control systems accordingly. The companion web and mobile applications allow farmers to remotely monitor conditions, receive alerts, adjust settings, and view historical data and growth analytics. The system uses machine learning to optimize growing conditions based on plant type and growth stage, resulting in up to 30% increased yield and 40% reduced water usage compared to traditional methods.",
    //     image: "/projects/hydroponics-system.jpg",
    //     tags: ["Mechanical", "IoT"],
    //     links: {
    //       github: "https://github.com/dhiraj-th/smart-hydroponics",
    //       live: null
    //     },
    //     technologies: ["Raspberry Pi", "Node.js", "React", "MongoDB", "Sensors", "3D Printing"]
    //   }
    // ];

    // Simulate API fetch
    // setTimeout(() => {
    //   setProjectsData(mockData);
    //   setFilteredProjects(mockData);
    //   setLoading(false);
    // }, 500);
  }, []);

  useEffect(() => {
    // No need to filter here as filteredProjects is now derived directly
  }, [activeFilter, projects]);

  const toggleVisibility = () => {
    setVisibleCount(visibleCount === 3 ? filteredProjects.length : 3);
  };

  const openModal = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedProject(null);
  };

  // Helper to normalize project links
  function getProjectLinks(project) {
    // Prefer links object if present, otherwise build from github_url/live_url
    if (project.links && (project.links.github || project.links.live)) {
      return project.links;
    }
    return {
      github: project.github_url || '',
      live: project.live_url || ''
    };
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-6 bg-gray-700 w-1/3 rounded"></div>
        <div className="flex gap-2 mb-6">
          {[1, 2, 3, 4].map((index) => (
            <div key={index} className="h-10 bg-gray-700 w-20 rounded-full"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((index) => (
            <div key={index} className="bg-gray-800 rounded-lg overflow-hidden">
              <div className="h-48 bg-gray-700"></div>
              <div className="p-6 space-y-3">
                <div className="h-5 bg-gray-700 w-3/4 rounded"></div>
                <div className="h-4 bg-gray-700 w-full rounded"></div>
                <div className="h-4 bg-gray-700 w-5/6 rounded"></div>
                <div className="flex gap-2">
                  <div className="h-4 bg-gray-700 w-16 rounded-full"></div>
                  <div className="h-4 bg-gray-700 w-16 rounded-full"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <h3 className="text-2xl font-semibold text-white mb-8 flex items-center gap-3">
        <FaLaptopCode className="text-primary" />
        Projects
      </h3>

      {/* Category filter buttons */}
      <div className="flex flex-wrap gap-2 mb-8">
        <motion.button
          key="all"
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            activeFilter === 'all'
              ? 'bg-primary text-white'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
          onClick={() => setActiveFilter('all')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          All
        </motion.button>
        {allCategories.map((cat, index) => (
          <motion.button
            key={cat}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeFilter === cat
                ? 'bg-primary text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            onClick={() => setActiveFilter(cat)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {cat}
          </motion.button>
        ))}
      </div>

      {/* Single grid of filtered projects */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        layout
      >
        <AnimatePresence>
          {visibleProjects.slice(0, visibleCount).map((project) => (
            <motion.div
              key={project.id}
              className="bg-secondary/30 rounded-lg overflow-hidden border border-primary/10 hover:border-primary/30 transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.5 }}
              layout
            >
              <div className="h-48 overflow-hidden relative">
                <img 
                  src={project.image_url || project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/20 to-transparent"></div>
              </div>
              <div className="p-6">
                <h4 className="text-xl font-medium text-white mb-3">{project.title}</h4>
                <p className="text-gray-300 mb-4 line-clamp-3">{project.description}</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(project.categories || []).map((cat, idx) => (
                    <span key={idx} className="bg-primary/10 text-primary px-3 py-1 rounded-full text-xs">
                      {cat}
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mb-6">
                  {(project.tags || []).map((tag, index) => (
                    <span 
                      key={index} 
                      className="bg-primary/10 text-primary px-3 py-1 rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => openModal(project)}
                    className="text-primary hover:underline"
                  >
                    View Details
                  </button>
                  <div className="flex gap-3">
                    {getProjectLinks(project).github && (
                      <a 
                        href={getProjectLinks(project).github} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-primary transition-colors"
                        aria-label="GitHub repository"
                      >
                        <FaGithub size={18} />
                      </a>
                    )}
                    {getProjectLinks(project).live && (
                      <a 
                        href={getProjectLinks(project).live} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-primary transition-colors"
                        aria-label="Live demo"
                      >
                        <FaExternalLinkAlt size={16} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {filteredProjects.length > 3 && (
        <div className="text-center mt-8">
          <button
            onClick={toggleVisibility}
            className="flex items-center gap-2 mx-auto px-4 py-2 bg-primary/10 text-primary rounded-full hover:bg-primary/20 transition-colors"
          >
            {visibleCount === 3 ? (
              <>
                See More <FaChevronDown />
              </>
            ) : (
              <>
                See Less <FaChevronUp />
              </>
            )}
          </button>
        </div>
      )}

      {selectedProject && (
        <Modal
          isOpen={modalOpen}
          onClose={closeModal}
          title={selectedProject.title}
          content={
            <div>
              <p className="mb-4">{selectedProject.longDescription || selectedProject.description}</p>
              {selectedProject.categories && selectedProject.categories.length > 0 && (
                <div className="mb-2 text-sm text-gray-400">
                  <strong>Categories:</strong> {selectedProject.categories.join(', ')}
                </div>
              )}
              {selectedProject.tags && selectedProject.tags.length > 0 && (
                <div className="mb-2 text-sm text-gray-400">
                  <strong>Tags:</strong> {selectedProject.tags.join(', ')}
                </div>
              )}
              {selectedProject.tech_stack && (
                <div className="mb-2 text-sm text-gray-400">
                  <strong>Tech Stack:</strong> {selectedProject.tech_stack}
                </div>
              )}
              {/* External link buttons removed; rely on Modal's links prop */}
            </div>
          }
          image={selectedProject.image_url || selectedProject.image}
          links={getProjectLinks(selectedProject)}
          technologies={selectedProject.technologies}
        />
      )}
    </div>
  );
};

export default Projects; 